import React, { useState, useEffect } from 'react';
import GreetingScreen from './greeting/GreetingScreen';
import MatricParser from './parser/MatricParser';
import AnalyticsDashboard from './analytics/AnalyticsDashboard';
import SettingsPanel from './settings/SettingsPanel';
import TestPanel from './testing/TestPanel';
import AdminPanel from './admin/AdminPanel';
import TeamDashboard from './dashboard/TeamDashboard';
import TeamSelector from './TeamSelector';
import CatAnimation from './ui/CatAnimation/CatAnimation';
import TaskCard from './ui/TaskCard/TaskCard';
import teamService from '../services/teamService';
import { useApp } from '../shared/context/AppContext';
import './MainApp.css';

const MainApp = () => {
  const { theme, setTheme, selectedTeam, setSelectedTeam, csvData, setCsvData, settings } = useApp();
  // Состояние для управления экранами и данными
  const [currentScreen, setCurrentScreen] = useState(() => (
    settings.showGreeting ? 'greeting' : 'main'
  ));
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [appData, setAppData] = useState({
    isInitialized: false,
    userPreferences: {},
    currentView: 'table'
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  
  // Состояние для burger-menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!settings.showGreeting) {
      setCurrentScreen('main');
      setAppData((prev) => ({ ...prev, isInitialized: true }));
      if (!selectedTeam) {
        setShowTeamSelector(true);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Обработчики переходов между экранами
  const handleGreetingFinish = () => {
    if (selectedTeam) {
      setCurrentScreen('main');
      setAppData(prev => ({ ...prev, isInitialized: true }));
    } else {
      setShowTeamSelector(true);
    }
  };

  const handleViewChange = (view) => {
    setAppData(prev => ({ ...prev, currentView: view }));
    // Закрываем мобильное меню при переходе
    setIsMobileMenuOpen(false);
  };

  const handleBackToGreeting = () => {
    if (settings.showGreeting) {
      setCurrentScreen('greeting');
      return;
    }

    setCurrentScreen('main');
    if (!selectedTeam) {
      setShowTeamSelector(true);
    }
  };

  // Обработчик выбора команды (упрощенная версия)
  const handleTeamSelect = async (teamName) => {
    try {
      setSelectedTeam(teamName);
      setShowTeamSelector(false);
      setCurrentScreen('main');
      setAppData(prev => ({ ...prev, isInitialized: true }));
      
      // Загружаем сохраненные шаги для команды
      await loadTeamSteps(teamName);
    } catch (error) {
      console.error('Ошибка выбора команды:', error);
      alert('Ошибка при выборе команды. Попробуйте еще раз.');
    }
  };

  const handleOpenTeamSelector = () => {
    setShowTeamSelector(true);
  };

  const loadTeamSteps = async (teamName) => {
    try {
      const teamData = await teamService.getTeam(teamName);
      if (teamData && teamData.steps) {
        // Шаги загружены успешно
      }
    } catch (error) {
      console.error('Ошибка загрузки шагов команды:', error);
    }
  };

  const saveTeamSteps = async (teamName, steps) => {
    try {
      await teamService.updateTeamSteps(teamName, steps);
      console.log(`✅ Шаги сохранены для команды ${teamName}: ${steps.length} шагов`);
    } catch (error) {
      console.error('Ошибка сохранения шагов:', error);
    }
  };

  const handleDataLoad = (data) => {
    setCsvData(data);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    document.body.className = newTheme === 'dark' ? 'dark-theme' : '';
  };

  const handleAdminPanelClick = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (adminPassword === 'admin123') {
      setIsAdminAuthenticated(true);
      setShowPasswordModal(false);
      setAdminPassword('');
      setAppData(prev => ({ ...prev, currentView: 'admin' }));
    } else {
      alert('Неверный пароль!');
      setAdminPassword('');
    }
  };

  // Обработчики для burger-menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileAdminClick = () => {
    setShowPasswordModal(true);
    closeMobileMenu();
  };

  const handleMobileTeamChange = () => {
    setShowTeamSelector(true);
    closeMobileMenu();
  };

  // Рендер компонентов в зависимости от текущего экрана
  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'greeting':
        return <GreetingScreen onFinish={handleGreetingFinish} />;
      
      case 'main':
        return (
          <div className="main-app-container">
            <header className="app-header">
              <div className="header-content">
                <div className="header-title">
                  <svg className="app-logo" width="40" height="47" viewBox="0 0 1417 1767" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M142.511 309.58L648.817 18.2242C664.588 10.4219 684.843 0.319336 714.385 0.319336C745.429 0.319336 757.697 7.22059 780.62 18.568L1283.86 308.273C1326.16 331.018 1351.52 375.78 1349.32 423.733V1027.28C1351.51 1075.08 1323.23 1113.76 1286.68 1141.39L778.557 1433.9C756.905 1445.27 738.744 1451.82 713.71 1451.82C687.283 1451.82 662.784 1443.57 642.633 1429.53L141.81 1141.22C103.584 1115.47 77.9262 1076.53 79.3433 1029.25L79.2793 417.944C79.2793 372.978 103.434 331.698 142.511 309.58Z" fill="#3BBEDB"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M344.979 457.041L672.025 268.841C682.212 263.801 695.296 257.275 714.378 257.275C734.431 257.275 742.356 261.734 757.163 269.063L1082.23 456.196C1109.55 470.889 1125.93 499.802 1124.51 530.777V920.636C1125.93 951.508 1107.66 976.496 1084.05 994.343L755.83 1183.29C741.843 1190.63 730.113 1194.86 713.942 1194.86C696.872 1194.86 681.047 1189.54 668.03 1180.46L606.129 1144.83L392.221 1285.37L394.004 1022.71L344.526 994.23C319.835 977.599 303.261 952.448 304.177 921.905L304.135 527.037C304.135 497.993 319.738 471.328 344.979 457.041Z" fill="white"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M470.513 605.29H537.424C548.189 605.29 556.995 614.097 556.995 624.861V907.194C556.995 917.96 548.189 926.766 537.424 926.766H470.513C459.748 926.766 450.941 917.96 450.941 907.194V624.861C450.941 614.097 459.748 605.29 470.513 605.29Z" fill="#3BBEDB"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M613.022 679.859H679.934C690.698 679.859 699.505 688.666 699.505 699.431V907.195C699.505 917.96 690.698 926.767 679.934 926.767H613.022C602.258 926.767 593.451 917.96 593.451 907.195V699.431C593.451 688.666 602.258 679.859 613.022 679.859Z" fill="#3BBEDB"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M755.533 751.91H822.443C833.208 751.91 842.015 760.717 842.015 771.481V907.195C842.015 917.96 833.208 926.767 822.443 926.767H755.533C744.768 926.767 735.961 917.96 735.961 907.195V771.481C735.961 760.717 744.768 751.91 755.533 751.91Z" fill="#3BBEDB"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M891.415 534.035H958.326C969.091 534.035 977.898 542.843 977.898 553.607V907.195C977.898 917.96 969.091 926.767 958.326 926.767H891.415C880.651 926.767 871.844 917.96 871.844 907.195V553.607C871.844 542.843 880.651 534.035 891.415 534.035Z" fill="#3BBEDB"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M743.774 637.714H767.818V613.103C767.818 608.806 771.334 605.29 775.631 605.29H802.344C806.641 605.29 810.157 608.806 810.157 613.103V637.714H834.201C838.499 637.714 842.015 641.23 842.015 645.527V672.24C842.015 676.538 838.499 680.053 834.201 680.053H810.157V704.664C810.157 708.961 806.641 712.477 802.344 712.477H775.631C771.334 712.477 767.818 708.961 767.818 704.664V680.053H743.774C739.477 680.053 735.961 676.538 735.961 672.24V645.527C735.961 641.23 739.477 637.714 743.774 637.714Z" fill="#3BBEDB"/>
                  </svg>
                  <h1>Анализ медицинских процессов</h1>
                </div>
                <nav className="header-nav">
                  <button 
                    className={`nav-btn ${appData.currentView === 'table' ? 'active' : ''}`}
                    onClick={() => handleViewChange('table')}
                  >
                    📊 Таблица
                  </button>
                  <button 
                    className={`nav-btn ${appData.currentView === 'dashboard' ? 'active' : ''}`}
                    onClick={() => handleViewChange('dashboard')}
                  >
                    📈 Дашборды
                  </button>
                  <button 
                    className="nav-btn restart-btn"
                    onClick={handleBackToGreeting}
                  >
                    🔄 Перезапуск
                  </button>
                </nav>
                <div className="header-controls">
                  <button 
                    className="admin-panel-btn"
                    onClick={handleAdminPanelClick}
                    title="Админ-панель (требует пароль)"
                  >
                    👨‍💼 Админ-панель
                  </button>
                  {selectedTeam && (
                    <div className="team-display">
                      <span className="team-label">Команда:</span>
                      <span className="team-name">{selectedTeam}</span>
                      <button 
                        className="change-team-btn"
                        onClick={handleOpenTeamSelector}
                        title="Сменить команду"
                      >
                        🔄
                      </button>
                    </div>
                  )}
                  <button 
                    className="theme-toggle-btn"
                    onClick={() => handleThemeChange(theme === 'light' ? 'dark' : 'light')}
                    title={theme === 'light' ? 'Переключить на темную тему' : 'Переключить на светлую тему'}
                  >
                    {theme === 'light' ? '🌙' : '☀️'}
                  </button>
                  <button 
                    className={`burger-menu ${isMobileMenuOpen ? 'active' : ''}`}
                    onClick={toggleMobileMenu}
                    title="Меню"
                  >
                    <div className="burger-line"></div>
                    <div className="burger-line"></div>
                    <div className="burger-line"></div>
                  </button>
                </div>
              </div>
              {settings.showAnimations && (
                <div className="main-cat">
                  <CatAnimation selectedTeam={selectedTeam} theme={theme} />
                </div>
              )}
            </header>

            {/* Мобильное меню */}
            <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}>
              <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
                <div className="mobile-menu-header">
                  <h3>Меню</h3>
                  <button className="mobile-menu-close" onClick={closeMobileMenu}>
                    ✕
                  </button>
                </div>
                
                {selectedTeam && (
                  <div className="mobile-team-display">
                    <div className="team-label">Команда:</div>
                    <div className="team-name">{selectedTeam}</div>
                    <button 
                      className="mobile-admin-btn"
                      onClick={handleMobileTeamChange}
                    >
                      🔄 Сменить команду
                    </button>
                  </div>
                )}
                
                <button 
                  className="mobile-admin-btn"
                  onClick={handleMobileAdminClick}
                >
                  👨‍💼 Админ-панель
                </button>
                
                <div className="mobile-menu-item" onClick={() => handleViewChange('table')}>
                  📊 Таблица
                </div>
                
                <div className="mobile-menu-item" onClick={() => handleViewChange('dashboard')}>
                  📈 Дашборды
                </div>
                
                <div className="mobile-menu-item" onClick={handleBackToGreeting}>
                  🔄 Перезапуск
                </div>
              </div>
            </div>

            <main className="app-main">
              {appData.currentView === 'table' && (
                <div className="content-section">
                    <TaskCard selectedTeam={selectedTeam} theme={theme} />
                    <MatricParser
                    onDataLoad={handleDataLoad} 
                    theme={theme}
                    selectedTeam={selectedTeam}
                    onStepsChange={(steps) => selectedTeam && saveTeamSteps(selectedTeam, steps)}
                    onNavigateToDashboard={() => {
                      handleViewChange('dashboard');
                      setDashboardRefreshKey(prev => prev + 1);
                    }}
                  />
                </div>
              )}
              
              {appData.currentView === 'dashboard' && (
                <div className="content-section">
                  <TeamDashboard 
                    selectedTeam={selectedTeam}
                    theme={theme}
                    csvData={csvData}
                    key={`dashboard-${selectedTeam}-${dashboardRefreshKey}`}
                  />
                </div>
              )}
              
              {appData.currentView === 'analytics' && (
                <div className="content-section">
                  <AnalyticsDashboard data={csvData} theme={theme} />
                </div>
              )}
              
              {appData.currentView === 'settings' && (
                <div className="content-section">
                  <SettingsPanel />
                </div>
              )}
              
              {appData.currentView === 'testing' && (
                <div className="content-section">
                  <TestPanel />
                </div>
              )}
              
              {appData.currentView === 'admin' && (
                <div className="content-section">
                  <AdminPanel 
                    theme={theme}
                    onThemeChange={handleThemeChange}
                    csvData={csvData}
                    onBackToMain={() => setAppData(prev => ({ ...prev, currentView: 'table' }))}
                  />
                </div>
              )}
            </main>

            <footer className="app-footer">
              <p>&copy; 2026 Республиканский медицинский информационно-аналитический центр</p>
            </footer>
          </div>
        );
      
      default:
        return <GreetingScreen onFinish={handleGreetingFinish} />;
    }
  };

  return (
    <div className={`main-app ${theme === 'dark' ? 'dark-theme' : ''}`}>
      {renderCurrentScreen()}
      {showTeamSelector && (
        <TeamSelector onTeamSelect={handleTeamSelect} onClose={() => setShowTeamSelector(false)} />
      )}
      
      {/* Анимированная кошка */}
      {/* <CatAnimation selectedTeam={selectedTeam} theme={theme} /> */}
      
      {/* Модальное окно для ввода пароля админ-панели */}
      {showPasswordModal && (
        <div className="password-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPasswordModal(false)}>
          <div className="password-modal">
            <div className="password-modal-header">
              <h3>🔐 Доступ к админ-панели</h3>
              <button 
                className="password-modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="password-input-group">
                <label htmlFor="adminPassword">Введите пароль:</label>
                <input
                  type="password"
                  id="adminPassword"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Введите пароль..."
                  required
                  autoFocus
                />
                {/* <small>Пароль: admin123</small> */}
              </div>
              <div className="password-form-actions">
                <button type="button" onClick={() => setShowPasswordModal(false)}>
                  Отмена
                </button>
                <button type="submit">
                  Войти
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainApp; 